import type { CSSProperties } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT } from '@/shared/i18n'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  MAX_FILE_MB,
  REDACTION_DPI,
  inspectPdfForRedaction,
  redactPdf,
  redactedFilename,
  validateFileSize,
  type PdfPagePreview,
  type RedactionBox,
  type RedactionInspection,
  type RedactionProgress,
} from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'
import { RevealOnMount } from '@/shared/ui/RevealOnMount'

const UTILITY = getUtilityDefinition('redact-pdf').analyticsKey
const MIN_BOX_SIZE = 0.01

type Phase =
  | { kind: 'idle' }
  | { kind: 'reading'; file: File }
  | { kind: 'ready'; file: File; inspection: RedactionInspection }
  | { kind: 'loading'; file: File; inspection: RedactionInspection; progress: RedactionProgress }
  | {
      kind: 'success'
      file: File
      blob: Blob
      objectUrl: string
      pageCount: number
      boxCount: number
    }
  | { kind: 'error'; file: File | null; message: string; errorCode: string }

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.common.pickPdf}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(e.dataTransfer.files)
      }}
      className={cn(
        'flex cursor-pointer select-none flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary/60"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>

      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground/80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {t.common.localBadge}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
      />
    </div>
  )
}

interface FileSummaryProps {
  file: File
  inspection: RedactionInspection
  boxCount: number
  onClear: () => void
  disabled: boolean
}

function FileSummary({ file, inspection, boxCount, onClear, disabled }: FileSummaryProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-semibold text-primary">
        PDF
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">
          {formatBytes(file.size)} · {format(t.redactPdf.pageCount, { pages: inspection.pageCount })} ·{' '}
          {format(t.redactPdf.boxCount, { count: boxCount })}
        </p>
      </div>
      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  )
}

interface DraftBox {
  x: number
  y: number
  width: number
  height: number
}

interface PagePreviewProps {
  page: PdfPagePreview
  boxes: RedactionBox[]
  disabled: boolean
  onAddBox: (box: RedactionBox) => void
  onRemoveBox: (id: string) => void
}

function PagePreview({ page, boxes, disabled, onAddBox, onRemoveBox }: PagePreviewProps) {
  const t = useT()
  const previewRef = useRef<HTMLDivElement>(null)
  const [draft, setDraft] = useState<{
    startX: number
    startY: number
    currentX: number
    currentY: number
  } | null>(null)

  useEffect(() => {
    if (!draft || disabled) return undefined

    function handleMove(event: PointerEvent) {
      const point = pointerToRelativePoint(event, previewRef.current)
      if (!point) return
      setDraft((current) => current && { ...current, currentX: point.x, currentY: point.y })
    }

    function handleUp(event: PointerEvent) {
      const point = pointerToRelativePoint(event, previewRef.current)
      const currentDraft = draft
      setDraft(null)
      if (!point || !currentDraft) return

      const box = normalizeDraftBox({
        ...currentDraft,
        currentX: point.x,
        currentY: point.y,
      })
      if (box.width < MIN_BOX_SIZE || box.height < MIN_BOX_SIZE) return

      onAddBox({
        id: `${page.pageNumber}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        pageNumber: page.pageNumber,
        ...box,
      })
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp, { once: true })
    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
    }
  }, [disabled, draft, onAddBox, page.pageNumber])

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (disabled || event.button !== 0) return
    const point = pointerToRelativePoint(event.nativeEvent, previewRef.current)
    if (!point) return
    setDraft({
      startX: point.x,
      startY: point.y,
      currentX: point.x,
      currentY: point.y,
    })
  }

  const draftBox = draft ? normalizeDraftBox(draft) : null

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-medium text-foreground">
          {format(t.redactPdf.pageLabel, { page: page.pageNumber })}
        </span>
        <span className="text-xs text-muted-foreground">
          {format(t.redactPdf.pageBoxCount, { count: boxes.length })}
        </span>
      </div>
      <div
        ref={previewRef}
        role="img"
        aria-label={format(t.redactPdf.previewAria, { page: page.pageNumber })}
        onPointerDown={handlePointerDown}
        className={cn(
          'relative mx-auto max-w-full overflow-hidden rounded-lg border bg-background shadow-sm',
          disabled ? 'cursor-not-allowed opacity-70' : 'cursor-crosshair',
        )}
        style={{
          aspectRatio: `${page.width} / ${page.height}`,
          width: 'min(100%, 720px)',
        }}
      >
        <img
          src={page.dataUrl}
          alt=""
          className="h-full w-full select-none object-contain"
          draggable={false}
        />

        {boxes.map((box) => (
          <button
            key={box.id}
            type="button"
            aria-label={t.redactPdf.removeBox}
            title={t.redactPdf.removeBox}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => onRemoveBox(box.id)}
            disabled={disabled}
            className="absolute border border-white/80 bg-black/90 text-[10px] font-semibold text-white shadow-sm outline-none ring-offset-background transition focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none"
            style={boxToStyle(box)}
          >
            ×
          </button>
        ))}

        {draftBox && (
          <div
            className="absolute border border-white/80 bg-black/80 shadow-sm"
            style={boxToStyle(draftBox)}
          />
        )}
      </div>
    </div>
  )
}

function GuidancePanel() {
  const t = useT()

  return (
    <Card className="border-amber-300/60 bg-amber-50/70 dark:border-amber-400/30 dark:bg-amber-500/10">
      <CardContent className="space-y-2 p-4 text-sm">
        <p className="font-medium text-amber-900 dark:text-amber-100">{t.redactPdf.limitsTitle}</p>
        <p className="text-amber-900/80 dark:text-amber-100/80">{t.redactPdf.limitsBody}</p>
      </CardContent>
    </Card>
  )
}

function ProgressPanel({ progress }: { progress: RedactionProgress }) {
  const t = useT()
  const ratio =
    progress.total === 0 ? 0 : Math.min(100, Math.round((progress.current / progress.total) * 100))

  return (
    <div className="rounded-lg border bg-accent/20 p-4">
      <div className="mb-2 flex items-center justify-between text-sm">
        <span className="font-medium text-foreground">
          {progress.total === 0
            ? t.redactPdf.openingPdf
            : format(t.redactPdf.pageOf, { current: progress.current, total: progress.total })}
        </span>
        <span className="text-muted-foreground">{ratio}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-border">
        <div
          className="h-full bg-primary transition-[width] duration-150 ease-out"
          style={{ width: `${ratio}%` }}
        />
      </div>
    </div>
  )
}

interface ResultPanelProps {
  file: File
  blob: Blob
  objectUrl: string
  pageCount: number
  boxCount: number
  onReset: () => void
}

function ResultPanel({ file, blob, objectUrl, pageCount, boxCount, onReset }: ResultPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  function handleDownload() {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = redactedFilename(file.name)
    a.click()
  }

  return (
    <Card className="border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10">
      <CardContent className="p-5">
        <RevealOnMount />
        <div className="mb-4 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-green-600 dark:text-green-300"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="text-base font-semibold text-green-700 dark:text-green-200">{t.common.done}</span>
        </div>

        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.redactPdf.pagesProcessed}</span>
            <span className="font-medium text-foreground">{pageCount}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.redactPdf.boxesApplied}</span>
            <span className="font-medium text-foreground">{boxCount}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.size}</span>
            <span className="font-medium text-foreground">{formatBytes(blob.size)}</span>
          </div>
        </div>

        <p className="mb-4 rounded-md border border-green-300/60 bg-background/70 p-3 text-xs text-green-900 dark:border-green-400/30 dark:text-green-100">
          {t.redactPdf.resultNote}
        </p>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button onClick={handleDownload} size="lg" className="flex-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {t.common.download}
          </Button>
          <Button variant="outline" onClick={onReset} className="flex-1 sm:flex-none">
            {t.redactPdf.again}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function ErrorPanel({ message, onRetry }: { message: string; onRetry: () => void }) {
  const t = useT()
  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-destructive"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-sm font-semibold text-destructive">{t.common.errorTitle}</span>
        </div>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

export function RedactPdfForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [boxes, setBoxes] = useState<RedactionBox[]>([])
  const abortRef = useRef<AbortController | null>(null)

  const isBusy = phase.kind === 'reading' || phase.kind === 'loading'
  const editablePhase =
    phase.kind === 'ready' || phase.kind === 'loading' ? phase : null

  function cleanupSuccessUrl() {
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }
  }

  async function handleFile(file: File) {
    cleanupSuccessUrl()
    setBoxes([])

    if (!validateFileSize(file)) {
      toolError(UTILITY, 'client_too_large')
      setPhase({
        kind: 'error',
        file,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }

    setPhase({ kind: 'reading', file })

    try {
      const inspection = await inspectPdfForRedaction(file)
      setPhase({ kind: 'ready', file, inspection })
    } catch (err: unknown) {
      const code = classifyErrorCode(err)
      toolError(UTILITY, code)
      setPhase({
        kind: 'error',
        file,
        errorCode: code,
        message: errorMessage(code, t.redactPdf),
      })
    }
  }

  function handleClear() {
    cleanupSuccessUrl()
    abortRef.current?.abort()
    setBoxes([])
    setPhase({ kind: 'idle' })
  }

  function handleRetry() {
    if (phase.kind === 'error' && phase.file) {
      void handleFile(phase.file)
      return
    }
    handleClear()
  }

  function handleAddBox(box: RedactionBox) {
    setBoxes((current) => [...current, box])
  }

  function handleRemoveBox(id: string) {
    setBoxes((current) => current.filter((box) => box.id !== id))
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready') return
    if (boxes.length === 0) {
      toolError(UTILITY, 'no_boxes')
      setPhase({
        kind: 'error',
        file: phase.file,
        errorCode: 'no_boxes',
        message: t.redactPdf.noBoxes,
      })
      return
    }

    const controller = new AbortController()
    abortRef.current = controller
    setPhase({
      kind: 'loading',
      file: phase.file,
      inspection: phase.inspection,
      progress: { current: 0, total: phase.inspection.pageCount },
    })
    toolInvoke(UTILITY, {
      pages: phase.inspection.pageCount,
      boxes: boxes.length,
      render_dpi: REDACTION_DPI,
      mode: 'rasterize',
    })
    const startMs = Date.now()

    try {
      const result = await redactPdf({
        file: phase.file,
        boxes,
        signal: controller.signal,
        onProgress: (progress) => {
          setPhase((current) => (current.kind === 'loading' ? { ...current, progress } : current))
        },
      })
      const objectUrl = URL.createObjectURL(result.blob)
      const durationMs = Date.now() - startMs

      toolSuccess(UTILITY, durationMs, {
        pages: result.pageCount,
        boxes: boxes.length,
        output_bytes: result.blob.size,
        render_dpi: REDACTION_DPI,
        mode: 'rasterize',
      })

      setPhase({
        kind: 'success',
        file: phase.file,
        blob: result.blob,
        objectUrl,
        pageCount: result.pageCount,
        boxCount: boxes.length,
      })
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      const code = classifyErrorCode(err)
      toolError(UTILITY, code, { boxes: boxes.length, render_dpi: REDACTION_DPI })
      setPhase({
        kind: 'error',
        file: phase.file,
        errorCode: code,
        message: errorMessage(code, t.redactPdf),
      })
    }
  }

  return (
    <div className="space-y-6">
      <DropZone onFile={(file) => void handleFile(file)} disabled={isBusy} />
      <GuidancePanel />

      {phase.kind === 'reading' && (
        <div className="flex items-center gap-2 rounded-lg border bg-accent/20 p-4 text-sm text-muted-foreground">
          <Spinner />
          {t.redactPdf.reading}
        </div>
      )}

      {editablePhase && (
        <>
          <FileSummary
            file={editablePhase.file}
            inspection={editablePhase.inspection}
            boxCount={boxes.length}
            onClear={handleClear}
            disabled={isBusy}
          />

          <Card>
            <CardContent className="space-y-5 p-4 sm:p-5">
              <div>
                <p className="text-sm font-medium text-foreground">{t.redactPdf.editorTitle}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t.redactPdf.editorHint}</p>
              </div>

              <div className="space-y-6">
                {editablePhase.inspection.pages.map((page) => (
                  <PagePreview
                    key={page.pageNumber}
                    page={page}
                    boxes={boxes.filter((box) => box.pageNumber === page.pageNumber)}
                    disabled={isBusy}
                    onAddBox={handleAddBox}
                    onRemoveBox={handleRemoveBox}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {phase.kind === 'loading' && <ProgressPanel progress={phase.progress} />}

          {boxes.length === 0 && !isBusy && (
            <p className="rounded-lg border border-amber-300/60 bg-amber-50/70 px-4 py-3 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-100">
              {t.redactPdf.noBoxesHint}
            </p>
          )}

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              onClick={() => void handleSubmit()}
              disabled={isBusy || boxes.length === 0}
              size="lg"
              className="flex-1"
            >
              {isBusy && <Spinner />}
              {isBusy ? t.redactPdf.loading : t.redactPdf.submit}
            </Button>
            <Button variant="outline" onClick={handleClear} disabled={isBusy}>
              {t.redactPdf.clear}
            </Button>
          </div>
        </>
      )}

      {phase.kind === 'success' && (
        <ResultPanel
          file={phase.file}
          blob={phase.blob}
          objectUrl={phase.objectUrl}
          pageCount={phase.pageCount}
          boxCount={phase.boxCount}
          onReset={handleClear}
        />
      )}

      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={handleRetry} />}
    </div>
  )
}

function pointerToRelativePoint(
  event: PointerEvent,
  element: HTMLDivElement | null,
): { x: number; y: number } | null {
  const rect = element?.getBoundingClientRect()
  if (!rect) return null

  return {
    x: clamp((event.clientX - rect.left) / rect.width, 0, 1),
    y: clamp((event.clientY - rect.top) / rect.height, 0, 1),
  }
}

function normalizeDraftBox(draft: {
  startX: number
  startY: number
  currentX: number
  currentY: number
}): DraftBox {
  const x = Math.min(draft.startX, draft.currentX)
  const y = Math.min(draft.startY, draft.currentY)
  return {
    x,
    y,
    width: Math.abs(draft.currentX - draft.startX),
    height: Math.abs(draft.currentY - draft.startY),
  }
}

function boxToStyle(box: DraftBox): CSSProperties {
  return {
    left: `${box.x * 100}%`,
    top: `${box.y * 100}%`,
    width: `${box.width * 100}%`,
    height: `${box.height * 100}%`,
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function classifyErrorCode(err: unknown): string {
  const text = err instanceof Error ? err.message : String(err)
  if (/password|encrypted/i.test(text)) return 'encrypted'
  if (/canvas-unavailable|encode-failed|out of memory/i.test(text)) return 'render_error'
  return 'parse_error'
}

function errorMessage(code: string, copy: ReturnType<typeof useT>['redactPdf']): string {
  if (code === 'encrypted') return copy.encrypted
  if (code === 'render_error') return copy.renderError
  if (code === 'no_boxes') return copy.noBoxes
  return copy.parseError
}
